/**
 * liquidMetalVolPipeline — mercury blobs that merge, separate, and
 * shimmer as if lit by a moving studio environment.
 *
 * 16 mercury blobs in 2D-projected orbits push a soft-min field onto
 * the screen. The field is iso-surfaced for a hard "skin". A pseudo-
 * normal from the field gradient drives a stylized environment
 * reflection: we sample an FBM "sky" whose hue cycles and whose
 * position rotates with the audio. Specular is a tight pow lobe
 * pointing toward a moving light source, and Fresnel rim glow lights
 * the edges of every blob.
 *
 * Audio modulates: overall light angle, blob radii (so they merge
 * harder on beats), and reflection brightness.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface LiquidMetalVolPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
vec2 hash22(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)),
           dot(p, vec2(269.5, 183.3)));
  return fract(sin(p) * 43758.5453);
}

float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise2(p);
    p *= 2.04; a *= 0.5;
  }
  return v;
}

// "Sky" environment used for reflection. Cycles hue, swirls in 2D.
vec3 env(vec2 dir) {
  float ang = atan(dir.y, dir.x);
  float h = 0.5 + 0.5 * sin(ang * 1.0);
  vec3 c1 = vec3(0.85, 0.92, 1.00);
  vec3 c2 = vec3(0.50, 0.65, 0.95);
  vec3 c3 = vec3(0.95, 0.85, 0.45);
  vec3 base = mix(c1, c2, h);
  base = mix(base, c3, 0.5 + 0.5 * sin(ang * 2.0 + dir.y * 1.4));
  // Add banded cloud-like features.
  base += vec3(0.10, 0.15, 0.20) * fbm(dir * 2.5 + vec2(uTime * 0.05));
  // Bright "horizon".
  base += vec3(0.95, 0.75, 0.40) * smoothstep(0.05, -0.05, abs(dir.y));
  return base;
}

#define NBLOBS 12

// Metaball-style field for mercury blobs. Higher means inside metal.
float metalField(vec2 p, float t, float bass, float mid) {
  float field = 0.0;
  for (int i = 0; i < NBLOBS; i++) {
    float fi = float(i);
    float ax = 0.85 + 0.30 * hash21(vec2(fi, 11.0));
    float ay = 0.55 + 0.25 * hash21(vec2(fi, 13.0));
    float r1 = 1.4 + 0.3 * hash21(vec2(fi, 19.0));
    float r2 = 1.1 + 0.4 * hash21(vec2(fi, 23.0));
    float ph = hash21(vec2(fi, 27.0)) * 6.2832;
    vec2 c = vec2(
      ax * sin(r1 * t + ph),
      ay * sin(r2 * t + ph * 1.7)
    );
    // Blobs inflate / deflate on the beat.
    float r = 0.18 + 0.04 * hash21(vec2(fi, 31.0)) + 0.04 * bass * hash21(vec2(fi, 37.0));
    vec2 d = p - c;
    float dd = dot(d, d) + 0.001;
    field += (r * r) / dd;
  }
  return field;
}

vec2 metalGrad(vec2 p, float t, float bass, float mid) {
  float e = 0.003;
  float fx = metalField(p + vec2(e, 0.0), t, bass, mid);
  float fxn = metalField(p - vec2(e, 0.0), t, bass, mid);
  float fy = metalField(p + vec2(0.0, e), t, bass, mid);
  float fyn = metalField(p - vec2(0.0, e), t, bass, mid);
  return vec2(fx - fxn, fy - fyn) / (2.0 * e);
}

void main() {
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;

  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid  = (uMid.x  + uMid.y  + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid  = pow(clamp(mid  * 2.5, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 2.5, 0.0, 1.0), 0.7);

  float t = uTime * 0.55;

  // Background: dark slate gradient with iridescent film.
  vec3 bgTop = vec3(0.020, 0.020, 0.045);
  vec3 bgLow = vec3(0.045, 0.020, 0.040);
  vec3 col = mix(bgLow, bgTop, smoothstep(-0.6, 0.6, p.y));
  // Subtle floor reflection.
  float floorY = -0.55;
  float floorMask = smoothstep(0.02, -0.02, p.y - floorY);
  vec3 floorCol = mix(vec3(0.07, 0.06, 0.12), vec3(0.02, 0.02, 0.05),
                       fbm(p * 8.0 + vec2(t * 0.1, 0.0)));
  col = mix(col, floorCol, floorMask * 0.55);

  // Field: higher means inside. Iso-surface at threshold.
  float fld = metalField(p, t, bass, mid);
  vec2 grad = metalGrad(p, t, bass, mid);
  float isoThresh = 0.55 + 0.05 * sin(t * 0.5);
  float surfaceMask = smoothstep(isoThresh - 0.10, isoThresh + 0.10, fld);

  // Pseudo-normal.
  vec3 nrm3 = normalize(vec3(-grad, 1.0));
  // Reflection vector (camera looking down +z).
  vec3 viewDir = vec3(0.0, 0.0, 1.0);
  vec3 reflectDir = reflect(-viewDir, nrm3);
  vec2 rd = normalize(reflectDir.xy + 1e-5);

  // Light angle moves with audio.
  float lightAng = t * 0.7 + bass * 1.2 + mid * 0.6;
  vec3 lightDir = normalize(vec3(cos(lightAng), sin(lightAng * 1.3), 0.7));
  float specRaw = max(0.0, dot(reflectDir, lightDir));

  // Environment reflection sampled from the swirling sky.
  vec3 envCol = env(rd + vec2(0.0, t * 0.05));

  // Body color: dark to mid-gray mercury base + env reflection.
  vec3 mercury = vec3(0.65, 0.68, 0.72);
  vec3 reflected = envCol;
  float fres = pow(1.0 - max(0.0, nrm3.z), 3.5);
  vec3 base = mix(mercury, reflected, 0.5 + 0.4 * fres);

  // Specular highlight.
  float specPow = pow(specRaw, 24.0);
  vec3 specCol = vec3(1.0, 0.95, 0.85);
  base += specCol * specPow * (1.5 + bass * 1.4 + high * 1.8);

  // Rim glow.
  float rim = pow(1.0 - max(0.0, nrm3.z), 4.0);
  vec3 rimCol = mix(vec3(0.85, 0.95, 1.0),
                    vec3(1.0, 0.75, 0.45),
                    0.5 + 0.5 * sin(t * 0.5));
  base += rimCol * rim * (0.5 + high * 1.5);

  // Iridescent patches.
  float irid = sin(nrm3.x * 5.0 + nrm3.y * 4.0 + t * 1.3) * 0.5 + 0.5;
  vec3 iridCol = mix(vec3(0.95, 0.30, 0.65),
                     vec3(0.45, 0.85, 1.00),
                     irid);
  base += iridCol * 0.15 * (0.5 + high);

  col = mix(col, base, surfaceMask);

  // Drops that fly off when blobs collide (audio-driven).
  for (int i = 0; i < 14; i++) {
    float fi = float(i);
    float xPos = (hash21(vec2(fi, 41.0)) - 0.5) * 1.8;
    float yPos = -0.6 + 1.0 * fract(hash21(vec2(fi, 43.0)) * 2.0 + t * 0.6);
    vec2 dPos = vec2(xPos + sin(t * 1.5 + fi) * 0.1,
                     yPos + sin(t * 0.9 + fi * 1.3) * 0.05);
    float dd = length(p - dPos);
    float flyPhase = sin(t * (2.0 + fi * 0.2) + fi * 7.0);
    float dropMask = exp(-dd * 200.0) * (0.4 + 0.6 * bass) * (0.5 + 0.5 * flyPhase);
    col += vec3(0.85, 0.90, 1.0) * dropMask * 0.4;
  }

  // Audio overall tint.
  col += vec3(0.05, 0.10, 0.15) * bass * 0.20
       + vec3(0.10, 0.05, 0.15) * mid  * 0.15
       + vec3(0.10, 0.12, 0.08) * high * 0.10;

  // Vignette.
  col *= 1.0 - dot(p, p) * 0.30;

  // Tone map + gamma.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}
`;

export function computeLiquidMetalVolBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.7);
  out[1] = 0.6 + 0.4 * Math.abs(Math.sin(t * 1.4));
  out[2] = 0.4 + 0.3 * Math.sin(t * 2.2);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.0 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.6 + 2.3);
  out[5] = 0.4 + 0.4 * Math.sin(t * 2.3 + 3.1);
  out[6] = 0.3 + 0.5 * Math.sin(t * 3.7 + 0.5);
  out[7] = 0.4 + 0.4 * Math.sin(t * 5.4 + 1.7);
  out[8] = 0.3 + 0.5 * Math.sin(t * 7.4 + 2.1);
}

export function computeLiquidMetalVolRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const positions = [0.005, 0.020, 0.050, 0.150, 0.300, 0.500, 0.650, 0.800, 0.950];
  for (let i = 0; i < 9; i++) {
    let sum = 0, count = 0;
    for (let k = -2; k <= 2; k++) {
      const b = Math.floor(positions[i] * N) + k;
      if (b >= 0 && b < N) { sum += freqData[b]; count++; }
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createLiquidMetalVolPipeline(gl: WebGL2RenderingContext): LiquidMetalVolPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawLiquidMetalVol(
  gl: WebGL2RenderingContext,
  pipe: LiquidMetalVolPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  gl.uniform2f(gl.getUniformLocation(pipe.program, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(pipe.program, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
